/**
 * App: wires config, gate, consent, coordinator, SafeArea, telemetry and one Player per slot.
 * All browser APIs come in through `env`, so the whole app runs under jsdom with fakes.
 */
import { VERSION, buildConfig, getPlayerConfig, loadRemoteConfig } from './config.js';
import { createPageBudget } from './ad-budget.js';
import { parseSlotClips, resolvePlaylist } from './clips.js';
import { resolveConsent } from './consent.js';
import { createCoordinator, startPartnerGuard } from './coordinator.js';
import { checkPage, countWords, isVideoAllowedForSource, readTrafficSource, selectSlots, waitForInteraction } from './gate.js';
import { readCanonical, resolveDescriptionUrl } from './page-url.js';
import { createPlayer } from './player.js';
import { shouldUsePrebid } from './prebid.js';
import { createSafeArea, mobileAdDensity, AD_SELECTOR } from './safe-area.js';
import { pickProfile } from './size-profile.js';
import { createTelemetry, readCookie } from './telemetry.js';
import { injectStyles } from './ui.js';

/**
 * Add preconnect hints at gate time, never in the page head (PERF-3).
 * @param {Document} doc
 * @param {string[]} origins
 */
export function addPreconnect(doc, origins) {
  for (const origin of origins) {
    if (!origin || doc.querySelector(`link[rel="preconnect"][href="${origin}"]`)) continue;
    const link = doc.createElement('link');
    link.rel = 'preconnect';
    link.href = origin;
    doc.head.appendChild(link);
  }
}

/**
 * Create the application.
 * @param {object} env browser adapters (see index.js)
 * @returns {object} the window.QSVideoPlayer API
 */
export function createApp(env) {
  const { win, doc } = env;
  const app = {
    version: VERSION,
    config: null,
    players: [],
    coordinator: null,
    budget: null,
    consent: null,
    status: 'new',
    reason: '',
  };
  let debug = { log: () => {}, refresh: () => {}, outline: () => {} };
  let telemetry = null;
  let safeArea = null;
  let consentPromise = null;
  let traffic = { qsSrc: '', qsCmp: '' };
  let usePrebid = false;
  let abArm = '';
  let lazyObserver = null;
  const builtSlots = new WeakSet();
  const stops = [];

  /**
   * @param {string} type
   * @param {object} data
   */
  const log = (type, data) => debug.log('page', type, data);

  /**
   * Slots grouped per article (infinite scroll counts slots per article, §9.1).
   * @returns {Array<{article:Element, slots:Element[]}>}
   */
  function slotGroups() {
    const groups = new Map();
    for (const slot of doc.querySelectorAll('.qsvp-slot[data-qsvp-player]')) {
      const article = slot.closest(app.config.articleSelector);
      if (!article) continue; // C2: only slots inside the article body
      if (!groups.has(article)) groups.set(article, []);
      groups.get(article).push(slot);
    }
    return Array.from(groups, ([article, slots]) => ({ article, slots }));
  }

  /**
   * C21 mobile density check for the first `count` selected slots in an article.
   * @param {Element} article
   * @param {Element[]} candidateSlots
   * @returns {Function}
   */
  function densityCheck(article, candidateSlots) {
    return (count) => {
      const contentHeight = article.getBoundingClientRect().height;
      const adHeights = Array.from(article.querySelectorAll(AD_SELECTOR)).map((el) => el.getBoundingClientRect().height);
      for (const slot of candidateSlots.slice(0, count)) adHeights.push(slot.getBoundingClientRect().height);
      return mobileAdDensity({ contentHeight, adHeights }) <= app.config.locked.maxMobileAdDensity;
    };
  }

  /**
   * Build a player for a slot (lazy: only when within lazyMarginPx after the interaction).
   * @param {Element} slot
   * @param {Element} article
   * @param {number} index
   */
  function buildPlayer(slot, article, index) {
    if (builtSlots.has(slot)) return;
    builtSlots.add(slot);
    const config = app.config;
    const id = slot.getAttribute('data-qsvp-player');
    const onInvalid = (reason) => {
      log('clip_rejected', { id, reason });
      telemetry.track('qsvp_player_error', { player_id: id, error_code: 'clip_' + reason, stage: 'load' });
    };
    const slotClips = parseSlotClips(slot.getAttribute('data-qsvp-clips'), config.clips.cdnBase, onInvalid);
    const category = slot.getAttribute('data-qsvp-category') || '';
    const playlist = resolvePlaylist({ slotClips, category, clipsConfig: config.clips, onInvalid });
    const descriptionUrl = resolveDescriptionUrl({
      slotUrl: slot.getAttribute('data-qsvp-url'),
      canonicalHref: readCanonical(doc),
      location: win.location,
    });
    try {
      addPreconnect(doc, ['https://imasdk.googleapis.com', new URL(config.clips.cdnBase).origin]); // PERF-3
    } catch (error) {
      // Bad cdnBase: clips are rejected anyway.
    }
    const player = createPlayer({
      id,
      index,
      slot,
      env,
      config,
      playerConfig: getPlayerConfig(config, id) || { float: true },
      coordinator: app.coordinator,
      budget: app.budget,
      telemetry,
      safeArea,
      article,
      playlist,
      descriptionUrl,
      videoAllowed: isVideoAllowedForSource(config, traffic.qsSrc),
      usePrebid,
      custParams: { qs_src: traffic.qsSrc, qs_cmp: traffic.qsCmp, vcat: category },
      consentPromise,
      debug: (source, type, data) => debug.log(source, type, data),
      outline: (obstacles, rect) => debug.outline(obstacles, rect),
    });
    app.players.push(player);
    player.start();
  }

  /** Select slots and observe them for the lazy margin (§3 gate, §9). */
  function scan() {
    const config = app.config;
    const profileName = pickProfile(win.innerWidth, config.sizeProfiles);
    for (const group of slotGroups()) {
      const fresh = group.slots.filter((slot) => !slot.hasAttribute('data-qsvp-seen'));
      if (!fresh.length) continue;
      fresh.forEach((slot) => slot.setAttribute('data-qsvp-seen', ''));
      const slots = group.slots.map((el) => ({
        id: el.getAttribute('data-qsvp-player'),
        el,
        top: el.getBoundingClientRect().top + win.scrollY,
      }));
      const { selected, skipped } = selectSlots({
        slots,
        config,
        profileName,
        viewportHeight: win.innerHeight,
        article: group.article,
        articleWords: countWords(group.article),
        densityOk: densityCheck(group.article, group.slots),
      });
      log('slots_selected', { selected, skipped });
      group.slots.forEach((el, index) => {
        if (!selected.includes(el.getAttribute('data-qsvp-player'))) return;
        el.__qsvpArticle = group.article;
        el.__qsvpIndex = index;
        // 2.0.8 floatOnArrival: a slot the reader is already past is built now, so its player can start floated.
        if (config.floatOnArrival && el.getBoundingClientRect().bottom <= 0) buildPlayer(el, group.article, index);
        else lazyObserver.observe(el);
      });
    }
  }

  /** Page-level listeners: scroll/resize/orientation/visibility/pagehide/focus (§7.1, §7.2, §10). */
  function listenPage() {
    let resizeTimer = null;
    const each = (fn) => app.players.forEach((player) => fn(player));
    const onScroll = () => each((player) => player.onPageChange());
    const onResize = () => {
      each((player) => player.onPageChange());
      env.clearTimeout(resizeTimer);
      resizeTimer = env.setTimeout(() => each((player) => player.onViewportChange()), 150); // SZ-6 debounce
    };
    const onVisibility = () => each((player) => player.onTabVisibility(doc.visibilityState === 'hidden'));
    const onPageHide = () => each((player) => player.onPageHide());
    const onFocus = (event) => {
      const el = event.target;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable))
        each((player) => player.onInputFocus());
    };
    const add = (target, type, handler, options) => {
      target.addEventListener(type, handler, options);
      stops.push(() => target.removeEventListener(type, handler, options));
    };
    add(win, 'scroll', onScroll, { passive: true });
    add(win, 'resize', onResize, { passive: true });
    add(win, 'orientationchange', onResize, { passive: true });
    add(doc, 'visibilitychange', onVisibility);
    add(win, 'pagehide', onPageHide);
    add(doc, 'focusin', onFocus);
    const mutations = new env.MutationObserver((records) => {
      onScroll();
      // Infinite scroll: new slots appended below (§9.1).
      if (
        records.some((record) =>
          Array.from(record.addedNodes || []).some(
            (node) => node.nodeType === 1 && (node.matches('.qsvp-slot') || node.querySelector('.qsvp-slot')),
          ),
        )
      ) {
        scan();
      }
    });
    mutations.observe(doc.body, { childList: true, subtree: true });
    stops.push(() => mutations.disconnect());
  }

  /**
   * Debug mode add-on (?qsvp_debug=1). It ships as qs-video-player.debug.js next to the player so the
   * production file stays inside the §9 size budget; a missing add-on just means console-free QA.
   * @returns {Promise<object>}
   */
  async function loadDebug() {
    try {
      if (!env.createDebug && env.debugScriptUrl) await env.loadScript(env.debugScriptUrl, {});
      const factory = env.createDebug || (win.QSVideoPlayer && win.QSVideoPlayer.createDebug);
      if (factory) return factory({ doc, win, enabled: true, app });
    } catch (error) {
      // Debug add-on blocked: carry on without it.
    }
    return debug;
  }

  /**
   * Everything after the gate: consent, players, guards (also re-run on SPA route change).
   */
  function startPlayers() {
    const config = app.config;
    app.coordinator = createCoordinator({
      pendingWaitMs: config.video.pendingWaitMs,
      setTimeoutFn: env.setTimeout,
      clearTimeoutFn: env.clearTimeout,
      onEvent: (name, params) => {
        log(name, params);
        if (name === 'qsvp_token') telemetry.track('qsvp_token', { action: params.action, player_id: params.player_id });
      },
    });
    app.budget = createPageBudget(config.adSchedule);
    lazyObserver = new env.IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          lazyObserver.unobserve(entry.target);
          buildPlayer(entry.target, entry.target.__qsvpArticle, entry.target.__qsvpIndex);
        }
      },
      { rootMargin: `${config.lazyMarginPx}px 0px` }, // WF-2: within 300 px of the viewport
    );
    const ourRoots = () => app.players.map((player) => player.refs && player.refs.slot).filter(Boolean);
    const ignore = (config.partnerIgnoreSelectors || []).join(',');
    /** 2.0.9: muted media inside partnerIgnoreSelectors (Taboola thumbnails) is not a competing player. */
    const ignored = (el) => !!ignore && !!el.closest(ignore) && (el.tagName !== 'VIDEO' || el.muted);
    stops.push(
      startPartnerGuard({
        doc,
        MutationObserverCtor: env.MutationObserver,
        partnerSelectors: config.partnerPlayerSelectors,
        isOurs: (el) => ourRoots().some((root) => root.contains(el)) || !!el.closest('.qsvp-slot') || ignored(el),
        isInView: (el) => {
          const rect = el.getBoundingClientRect();
          return rect.bottom > 0 && rect.top < win.innerHeight && rect.width > 0 && rect.height > 0;
        },
        onForeignPlayer: (source) => {
          // 2.0.9: report the first block per page view, so its cost shows in GA4.
          if (!app.coordinator.isPartnerBlocked()) telemetry.track('qsvp_partner_block', { source });
          app.coordinator.partnerDetected(source); // C29
        },
      }),
    );
    scan();
  }

  /**
   * Initialise: config, eligibility, gate (§3), consent (§2.1), players.
   * @returns {Promise<void>}
   */
  app.init = async function init() {
    if (app.status !== 'new') return;
    app.status = 'loading';
    const pageConfig = win.QS_VIDEO_CONFIG || {};
    const draft = buildConfig(pageConfig, null, win.location.search);
    const remote = await loadRemoteConfig({ fetchFn: env.fetch, url: draft.remoteConfigUrl });
    const config = buildConfig(pageConfig, remote, win.location.search);
    app.config = config;
    if (config.debug) debug = await loadDebug();
    const page = checkPage({ win, doc, config });
    if (!page.eligible) {
      app.status = 'off';
      app.reason = page.reason;
      log('player_off', { reason: page.reason });
      return;
    }
    traffic = readTrafficSource({ search: win.location.search, storage: env.session });
    const abValue = readCookie(doc.cookie, config.abCookieName);
    abArm = abValue === 'control' ? 'control' : 'treatment';
    usePrebid = shouldUsePrebid(config, abValue);
    telemetry = createTelemetry({
      win,
      doc,
      config,
      now: env.now,
      setIntervalFn: env.setInterval,
      getCommon: () => ({
        site: config.video.custParams.site,
        qs_src: traffic.qsSrc,
        qs_cmp: traffic.qsCmp,
        ab_arm: abArm,
        prebid: usePrebid ? 1 : 0,
        player_version: VERSION,
      }),
      debugLog: (name, params) => debug.log('ga4', name, params),
    });
    safeArea = createSafeArea({
      win,
      doc,
      avoidSelectors: config.avoidSelectors,
      ResizeObserverCtor: env.ResizeObserver,
      MutationObserverCtor: env.MutationObserver,
      onChange: () => app.players.forEach((player) => player.onPageChange()),
    });
    injectStyles(doc);
    app.status = 'waiting_interaction';
    if (config.waitForInteraction) {
      await waitForInteraction({ win, setTimeoutFn: env.setTimeout, isTrusted: env.isTrusted, scroll: config.gateOnScroll });
    }
    // PRV-1: consent resolution starts at the gate; no IMA, Prebid or AdSense until it resolves.
    consentPromise = resolveConsent({
      win,
      requireCmp: config.privacy.requireCmp,
      timeoutMs: config.privacy.cmpTimeoutMs,
      childDirected: config.privacy.childDirected,
      setTimeoutFn: env.setTimeout,
    }).then((consent) => {
      app.consent = consent;
      telemetry.start(consent.analyticsAllowed); // TEL-6
      log('consent', consent);
      return consent;
    });
    app.status = 'running';
    listenPage();
    startPlayers();
  };

  /** Pick up slots appended later (infinite scroll, §9.1). */
  app.scan = () => app.status === 'running' && scan();

  /**
   * SPA route change (§9.1): destroy every IMA manager and GPT slot, release sources, end floats,
   * release the token, then rebuild for the new article.
   */
  app.onRouteChange = () => {
    if (app.status !== 'running') return;
    app.players.forEach((player) => player.destroy());
    app.players = [];
    if (lazyObserver) lazyObserver.disconnect();
    app.coordinator.reset();
    stops.splice(0).forEach((stop) => stop());
    for (const slot of doc.querySelectorAll('[data-qsvp-seen]')) slot.removeAttribute('data-qsvp-seen');
    listenPage();
    startPlayers();
  };

  /** Full teardown. */
  app.destroy = () => {
    app.players.forEach((player) => player.destroy());
    app.players = [];
    if (lazyObserver) lazyObserver.disconnect();
    stops.splice(0).forEach((stop) => stop());
    if (safeArea) safeArea.disconnect();
    app.status = 'destroyed';
  };

  /** Internals the debug add-on reads. */
  app.internals = () => ({ telemetryMode: telemetry ? telemetry.mode : '-', usePrebid });
  return app;
}
